import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarPlus, Check, Ban, Trash2, Clock, Pencil, RotateCcw, ChevronDown, UserPlus, X } from "lucide-react";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminNav } from "../components/admin/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 w-full rounded-md bg-seed-off px-4 text-sm text-seed-deep outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]";

const STATUS = {
  agendada: { label: "Agendada", cls: "bg-seed-salad/10 text-seed-salad" },
  realizada: { label: "Realizada", cls: "bg-seed-deep text-seed-off" },
  cancelada: { label: "Cancelada", cls: "bg-red-100 text-red-500" },
};

const fmt = (iso) => {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" }) +
    " às " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
};

const toLocalInput = (iso) => {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyForm = { lead_id: "", property_id: "", date: "", notes: "" };

const normTxt = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const ACTIVE_STATUSES = ["novo", "em_atendimento", "visita_agendada", "negociacao"];

const LeadPicker = ({ leads, value, onChange, onCreate, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [nc, setNc] = useState({ nome: "", telefone: "", email: "" });
  const [savingNew, setSavingNew] = useState(false);

  const sorted = useMemo(
    () =>
      [...leads].sort((a, b) => {
        const ai = ACTIVE_STATUSES.includes(a.status || "novo") ? 0 : 1;
        const bi = ACTIVE_STATUSES.includes(b.status || "novo") ? 0 : 1;
        if (ai !== bi) return ai - bi;
        return (a.nome || "").localeCompare(b.nome || "", "pt-BR");
      }),
    [leads]
  );

  const qn = normTxt(q);
  const qDigits = q.replace(/\D/g, "");
  const filtered = sorted.filter(
    (l) => normTxt(l.nome).includes(qn) || (qDigits.length > 0 && (l.telefone || "").replace(/\D/g, "").includes(qDigits))
  );
  const selected = leads.find((l) => l.id === value);
  const exactMatch = sorted.some((l) => normTxt(l.nome) === qn);

  const close = () => { setOpen(false); setCreating(false); setQ(""); };

  const submitNew = async () => {
    if (!nc.nome.trim() || !nc.telefone.trim() || savingNew) return;
    setSavingNew(true);
    const created = await onCreate({
      nome: nc.nome.trim(),
      telefone: nc.telefone.trim(),
      email: nc.email.trim() || null,
      origem: "agenda",
    });
    setSavingNew(false);
    if (created) close();
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className={`${inputCls} flex items-center justify-between gap-2 text-left`} data-testid={testId}>
        <span className={`truncate ${selected ? "" : "text-seed-deep/40"}`}>
          {selected ? `${selected.nome} · ${selected.telefone}` : "Contato..."}
        </span>
        <ChevronDown size={15} className={`shrink-0 text-seed-deep/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={close} />
          <div className="absolute z-50 mt-1 w-full min-w-72 rounded-md bg-white p-1.5 shadow-xl" data-testid={`${testId}-dropdown`}>
            {creating ? (
              <div className="p-2" data-testid={`${testId}-new-form`}>
                <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-seed-deep"><UserPlus size={13} className="text-seed-salad" /> Novo contato no CRM</p>
                <input autoFocus placeholder="Nome completo *" value={nc.nome} onChange={(e) => setNc((f) => ({ ...f, nome: e.target.value }))} className="mb-1.5 h-9 w-full rounded-md bg-seed-off px-3 text-sm text-seed-deep outline-none placeholder:text-seed-deep/40" data-testid={`${testId}-new-name`} />
                <input placeholder="Telefone/WhatsApp *" value={nc.telefone} onChange={(e) => setNc((f) => ({ ...f, telefone: e.target.value }))} className="mb-1.5 h-9 w-full rounded-md bg-seed-off px-3 text-sm text-seed-deep outline-none placeholder:text-seed-deep/40" data-testid={`${testId}-new-phone`} />
                <input placeholder="E-mail (opcional)" value={nc.email} onChange={(e) => setNc((f) => ({ ...f, email: e.target.value }))} className="mb-2 h-9 w-full rounded-md bg-seed-off px-3 text-sm text-seed-deep outline-none placeholder:text-seed-deep/40" data-testid={`${testId}-new-email`} />
                <div className="flex items-center gap-2">
                  <button type="button" onClick={submitNew} disabled={savingNew || !nc.nome.trim() || !nc.telefone.trim()} className="rounded-full bg-seed-deep px-4 py-2 text-xs font-bold text-seed-off transition-transform hover:scale-[1.03] disabled:opacity-50" data-testid={`${testId}-create-submit`}>
                    {savingNew ? "Criando..." : "Criar e selecionar"}
                  </button>
                  <button type="button" onClick={() => setCreating(false)} className="text-xs font-bold text-seed-deep/50 transition-colors hover:text-red-500" data-testid={`${testId}-create-cancel`}>
                    Voltar
                  </button>
                </div>
                <p className="mt-2 text-[10px] font-semibold text-seed-deep/40">O contato entra no CRM como "Novo" e já fica selecionado nesta visita.</p>
              </div>
            ) : (
              <>
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por nome ou telefone..."
                  className="mb-1 h-9 w-full rounded-md bg-seed-off px-3 text-sm text-seed-deep outline-none placeholder:text-seed-deep/40"
                  data-testid={`${testId}-search`}
                />
                <div className="max-h-56 overflow-y-auto">
                  {filtered.length === 0 && <p className="px-3 py-2 text-xs text-seed-deep/40">Nenhum contato encontrado.</p>}
                  {filtered.map((l) => {
                    const inactive = !ACTIVE_STATUSES.includes(l.status || "novo");
                    return (
                      <button key={l.id} type="button" onClick={() => { onChange(l.id); close(); }} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left transition-colors hover:bg-seed-off" data-testid={`${testId}-opt-${l.id}`}>
                        {value === l.id && <Check size={13} className="shrink-0 text-seed-salad" />}
                        <span className={`flex-1 truncate text-sm font-semibold ${inactive ? "text-seed-deep/40" : "text-seed-deep"}`}>{l.nome}</span>
                        {inactive && (
                          <span className="shrink-0 rounded-full bg-seed-deep/5 px-2 py-0.5 text-[9px] font-bold uppercase text-seed-deep/40">
                            {l.status === "ganho" ? "Ganho" : "Perdido"}
                          </span>
                        )}
                        <span className="shrink-0 text-[11px] font-semibold text-seed-deep/40">{l.telefone}</span>
                      </button>
                    );
                  })}
                </div>
                {q.trim() && !exactMatch && (
                  <button type="button" onClick={() => { setCreating(true); setNc({ nome: q.trim(), telefone: "", email: "" }); }} className="mt-0.5 flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-bold text-seed-salad transition-colors hover:bg-seed-off" data-testid={`${testId}-create-open`}>
                    <UserPlus size={13} className="shrink-0" /> Criar contato &#34;{q.trim()}&#34;
                  </button>
                )}
                {!q.trim() && (
                  <button type="button" onClick={() => { setCreating(true); setNc({ nome: "", telefone: "", email: "" }); }} className="mt-0.5 flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-bold text-seed-salad transition-colors hover:bg-seed-off" data-testid={`${testId}-create-open`}>
                    <UserPlus size={13} className="shrink-0" /> Criar novo contato
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default function AdminAgenda() {
  const { user, headers, logout } = useAdminAuth();
  const [visits, setVisits] = useState(null);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [v, l, p] = await Promise.all([
        axios.get(`${API}/crm/visits`, { headers: headers() }),
        axios.get(`${API}/crm/leads`, { headers: headers() }),
        axios.get(`${API}/properties`),
      ]);
      setVisits(v.data);
      setLeads(l.data);
      setProperties(p.data);
    } catch {
      setVisits([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const leadName = (id) => leads.find((l) => l.id === id)?.nome || "Contato removido";
  const propTitle = (id) => properties.find((p) => p.id === id)?.title || null;

  const createLead = async (payload) => {
    try {
      const r = await axios.post(`${API}/crm/leads`, payload, { headers: headers() });
      const created = { ...r.data, status: "novo", notes: [] };
      setLeads((ls) => [...ls, created]);
      setForm((f) => ({ ...f, lead_id: created.id }));
      toast.success(`Contato "${created.nome}" criado no CRM e selecionado!`);
      return created;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível criar o contato.");
      return null;
    }
  };

  const createVisit = async (e) => {
    e.preventDefault();
    if (!form.lead_id) {
      toast.error("Selecione ou crie um contato para a visita.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(
          `${API}/crm/visits/${editingId}`,
          {
            lead_id: form.lead_id,
            property_id: form.property_id,
            date: new Date(form.date).toISOString(),
            notes: form.notes,
          },
          { headers: headers() }
        );
        toast.success("Visita atualizada!");
        setEditingId(null);
      } else {
        await axios.post(
          `${API}/crm/visits`,
          {
            lead_id: form.lead_id,
            property_id: form.property_id || null,
            date: new Date(form.date).toISOString(),
            notes: form.notes || null,
          },
          { headers: headers() }
        );
        toast.success("Visita agendada! Aviso enviado por e-mail.");
      }
      setForm(emptyForm);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({
      lead_id: v.lead_id || "",
      property_id: v.property_id || "",
      date: toLocalInput(v.date),
      notes: v.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/crm/visits/${id}`, { status }, { headers: headers() });
      setVisits((vs) => vs.map((v) => (v.id === id ? { ...v, status } : v)));
    } catch {
      toast.error("Não foi possível atualizar a visita.");
    }
  };

  const removeVisit = async (id) => {
    if (!window.confirm("Excluir esta visita? Se o contato não tiver outra visita agendada, ele volta para 'Em atendimento' no CRM.")) return;
    try {
      await axios.delete(`${API}/crm/visits/${id}`, { headers: headers() });
      setVisits((vs) => vs.filter((v) => v.id !== id));
      toast.success("Visita excluída.");
    } catch {
      toast.error("Não foi possível excluir.");
    }
  };

  const hoje0 = new Date();
  hoje0.setHours(0, 0, 0, 0);
  const amanha0 = new Date(hoje0.getTime() + 86400000);
  const em7dias = new Date(hoje0.getTime() + 7 * 86400000);
  const agendadas = (visits || []).filter((v) => v.status === "agendada");
  const cntHoje = agendadas.filter((v) => { const d = new Date(v.date); return d >= hoje0 && d < amanha0; }).length;
  const cntSemana = agendadas.filter((v) => { const d = new Date(v.date); return d >= hoje0 && d < em7dias; }).length;
  const cntSemRetorno = agendadas.filter((v) => new Date(v.date) < hoje0).length;

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-6 pt-20 pb-24 lg:px-10" data-testid="admin-agenda">
      <AdminNav user={user} onLogout={logout} />
      <div className="mt-8 mb-10">
        <p className="text-sm font-bold uppercase tracking-[0.25em] text-seed-salad">Agenda</p>
        <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-seed-deep" data-testid="agenda-title">
          Visitas agendadas
        </h1>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="agenda-summary">
        <div className="rounded-lg bg-white p-4 text-center shadow-sm" data-testid="summary-hoje">
          <p className="font-heading text-2xl font-black text-seed-salad">{cntHoje}</p>
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Hoje</p>
        </div>
        <div className="rounded-lg bg-white p-4 text-center shadow-sm" data-testid="summary-semana">
          <p className="font-heading text-2xl font-black text-seed-deep">{cntSemana}</p>
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Nos próximos 7 dias</p>
        </div>
        <div className="rounded-lg bg-white p-4 text-center shadow-sm" data-testid="summary-sem-retorno">
          <p className={`font-heading text-2xl font-black ${cntSemRetorno > 0 ? "text-red-500" : "text-seed-deep"}`}>{cntSemRetorno}</p>
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Aguardando retorno</p>
        </div>
      </div>

      <form onSubmit={createVisit} className="mt-8 rounded-lg bg-white p-8 shadow-sm" data-testid="visit-form">
        <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-seed-deep">
          <CalendarPlus size={18} className="text-seed-salad" /> {editingId ? "Editar visita" : "Agendar visita"}
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <LeadPicker leads={leads} value={form.lead_id} onChange={(id) => setForm((f) => ({ ...f, lead_id: id }))} onCreate={createLead} testId="vf-lead" />
          <select value={form.property_id} onChange={set("property_id")} className={inputCls} data-testid="vf-property">
            <option value="">Imóvel (opcional)...</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
          <input required type="datetime-local" value={form.date} onChange={set("date")} className={inputCls} data-testid="vf-date" />
          <input placeholder="Observações (opcional)" value={form.notes} onChange={set("notes")} className={inputCls} data-testid="vf-notes" />
        </div>
        <div className="mt-5 flex gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="vf-submit"
            className="rounded-full bg-seed-deep px-8 py-3.5 font-heading text-sm font-bold text-seed-off transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
          >
            {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Agendar visita"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={() => { setEditingId(null); setForm(emptyForm); }}
              data-testid="vf-cancel-edit"
              className="flex items-center gap-2 rounded-full border border-seed-deep/20 px-6 py-3.5 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-red-400 hover:text-red-500"
            >
              <RotateCcw size={14} /> Cancelar edição
            </button>
          )}
        </div>
      </form>

      <div className="mt-8 space-y-3" data-testid="visit-list">
        {visits === null ? (
          <div className="flex justify-center py-10">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-seed-deep/10 border-t-seed-salad" />
          </div>
        ) : visits.length === 0 ? (
          <p className="rounded-lg bg-white p-10 text-center text-seed-deep/50 shadow-sm">Nenhuma visita agendada ainda.</p>
        ) : (
          visits.map((v) => (
            <div
              key={v.id}
              className={`flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white p-5 shadow-sm ${v.status !== "agendada" ? "opacity-60" : ""}`}
              data-testid={`visit-item-${v.id.slice(0, 8)}`}
            >
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-heading text-sm font-bold text-seed-deep">
                  <Clock size={14} className="text-seed-salad" /> {fmt(v.date)}
                </p>
                <p className="mt-1 text-sm text-seed-deep/70">
                  {leadName(v.lead_id)}
                  {propTitle(v.property_id) && <span className="text-seed-deep/50"> · {propTitle(v.property_id)}</span>}
                </p>
                {v.notes && <p className="mt-1 text-xs text-seed-deep/40">{v.notes}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS[v.status]?.cls || ""}`}>
                  {STATUS[v.status]?.label || v.status}
                </span>
                {v.status === "agendada" && (
                  <>
                    <button onClick={() => startEdit(v)} data-testid={`visit-edit-${v.id.slice(0, 8)}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-seed-salad hover:text-white" aria-label="Editar">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => setStatus(v.id, "realizada")} data-testid={`visit-done-${v.id.slice(0, 8)}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-seed-vivid" aria-label="Marcar realizada">
                      <Check size={15} />
                    </button>
                    <button onClick={() => setStatus(v.id, "cancelada")} data-testid={`visit-cancel-${v.id.slice(0, 8)}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-red-400 hover:text-white" aria-label="Cancelar">
                      <Ban size={15} />
                    </button>
                  </>
                )}
                <button onClick={() => removeVisit(v.id)} data-testid={`visit-delete-${v.id.slice(0, 8)}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-red-500 hover:text-white" aria-label="Excluir">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </main>
  );
}
